import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { UilabMatTabsModule } from '@uilab/ng-mat-components';
import { Highlight } from 'ngx-highlightjs';
import { HeaderComponent } from '../../shared/components/header/header.component';

interface IconGetStartedExampleTab {
    readonly id: 'standalone' | 'ngmodule';
    readonly label: string;
    readonly code: string;
    readonly language: 'typescript';
}

@Component({
    selector: 'avd-icon-get-started-page',
    templateUrl: './icon-get-started-page.component.html',
    styleUrls: ['./icon-get-started-page.component.scss'],
    standalone: true,
    imports: [Highlight, RouterLink, UilabMatTabsModule, HeaderComponent],
})
export class IconGetStartedPageComponent {
    readonly installCommand = 'npm install @uilab/sprites';

    readonly assetsSnippet = `"assets": [
  "src/favicon.ico",
  "src/assets",
  {
    "glob": "**/*.svg",
    "input": "node_modules/@uilab/sprites/sprites-styles",
    "output": "assets/sprites-styles"
  }
]`;

    readonly usageSnippet = `<uilab-mat-icon svgIcon="filled:action--copy-page_filled"></uilab-mat-icon>`;

    readonly registerTabs: readonly IconGetStartedExampleTab[] = [
        {
            id: 'standalone',
            label: 'Standalone',
            language: 'typescript',
            code: `import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UilabMatIconModule, UilabMatIconRegistry } from '@uilab/ng-mat-components';

function registerIconSets(iconRegistry: UilabMatIconRegistry, sanitizer: DomSanitizer) {
  return () => {
    iconRegistry.addSvgIconSetInNamespace('filled', sanitizer.bypassSecurityTrustResourceUrl('/assets/sprites-styles/uilab-icon-sprite-filled-symbols.svg'));
    iconRegistry.addSvgIconSetInNamespace('outlined', sanitizer.bypassSecurityTrustResourceUrl('/assets/sprites-styles/uilab-icon-sprite-outlined-symbols.svg'));
    iconRegistry.addSvgIconSetInNamespace('grayscale', sanitizer.bypassSecurityTrustResourceUrl('/assets/sprites-styles/uilab-icon-sprite-grayscale-symbols.svg'));
  };
}

providers: [
  importProvidersFrom(UilabMatIconModule),
  {
    provide: APP_INITIALIZER,
    useFactory: registerIconSets,
    deps: [UilabMatIconRegistry, DomSanitizer],
    multi: true,
  },
];`,
        },
        {
            id: 'ngmodule',
            label: 'NgModule',
            language: 'typescript',
            code: `import { APP_INITIALIZER, NgModule } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UilabMatIconModule, UilabMatIconRegistry } from '@uilab/ng-mat-components';

export function registerIconSets(iconRegistry: UilabMatIconRegistry, sanitizer: DomSanitizer) {
  return () => {
    iconRegistry.addSvgIconSetInNamespace('filled', sanitizer.bypassSecurityTrustResourceUrl('/assets/sprites-styles/uilab-icon-sprite-filled-symbols.svg'));
    iconRegistry.addSvgIconSetInNamespace('outlined', sanitizer.bypassSecurityTrustResourceUrl('/assets/sprites-styles/uilab-icon-sprite-outlined-symbols.svg'));
    iconRegistry.addSvgIconSetInNamespace('grayscale', sanitizer.bypassSecurityTrustResourceUrl('/assets/sprites-styles/uilab-icon-sprite-grayscale-symbols.svg'));
  };
}

@NgModule({
  imports: [UilabMatIconModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: registerIconSets,
      deps: [UilabMatIconRegistry, DomSanitizer],
      multi: true,
    },
  ],
})
export class AppModule {}`,
        },
    ];

    activeRegisterTab: IconGetStartedExampleTab['id'] = 'standalone';

    setRegisterTab(tabId: IconGetStartedExampleTab['id']): void {
        this.activeRegisterTab = tabId;
    }

    selectedRegisterTab(): IconGetStartedExampleTab {
        const tab = this.registerTabs.find(
            (item) => item.id === this.activeRegisterTab,
        );

        if (!tab) {
            return this.registerTabs[0];
        }

        return tab;
    }
}
